/* AlertWithClose — Dismissible alert banner */

import * as React from 'react'
import { useState } from 'react'
import { X, AlertCircle, CheckCircle2, AlertTriangle, Info } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/cn'

const alertVariants = cva(
  'relative flex items-start gap-3 rounded-lg px-4 py-3 text-[12.5px] border-l-[3px] transition-all',
  {
    variants: {
      variant: {
        info: 'bg-info-muted text-info border-l-info',
        success: 'bg-success-muted text-success border-l-success',
        warning: 'bg-warning-muted text-warning border-l-warning',
        destructive: 'bg-danger-muted text-danger border-l-destructive',
      },
    },
    defaultVariants: { variant: 'info' },
  },
)

const alertIcons = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  destructive: AlertCircle,
}

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  /** Allow user to dismiss */
  closable?: boolean
  onClose?: () => void
}

export function AlertWithClose({ className, variant = 'info', closable = true, onClose, children, ...props }: AlertProps) {
  const [visible, setVisible] = useState(true)
  if (!visible) return null

  const Icon = alertIcons[variant ?? 'info']

  return (
    <div className={cn(alertVariants({ variant }), className)} role="alert" {...props}>
      <Icon size={16} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">{children}</div>
      {closable && (
        <button
          onClick={() => { setVisible(false); onClose?.() }}
          className="shrink-0 p-0.5 rounded hover:bg-black/5 transition-colors"
        >
          <X size={14} />
        </button>
      )}
    </div>
  )
}
